/**
 * OKRDashboard Component
 * Organized view of data following OKR (Objectives & Key Results),
 * KPI (Key Performance Indicators), and CFR (Contributing Factors & Results) framework
 */

import { useState, useMemo } from 'react'
import {
  Target,
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  ChevronDown,
  Clock,
  Gauge,
  Users,
  BarChart3,
  Layers,
  GitBranch,
  Award,
  Flag
} from 'lucide-react'
import clsx from 'clsx'

// OKR Structure
interface Objective {
  id: string
  title: string
  description: string
  owner: string
  quarter: string
  progress: number
  status: 'on-track' | 'at-risk' | 'behind' | 'achieved' | 'off-track'
  keyResults: KeyResult[]
}

interface KeyResult {
  id: string
  title: string
  metric: string
  current: number
  target: number
  unit: string
  progress: number
  trend: 'up' | 'down' | 'stable'
  trendValue: number
  kpis: KPI[]
}

interface KPI {
  id: string
  name: string
  value: number
  target: number
  unit: string
  status: 'good' | 'warning' | 'critical'
  contributingFactors: ContributingFactor[]
}

interface ContributingFactor {
  id: string
  factor: string
  impact: 'positive' | 'negative' | 'neutral'
  weight: number
  currentValue: number
  optimalRange: { min: number; max: number }
  description: string
}

// Props interface
interface OKRDashboardProps {
  objectives: Objective[]
  period: string
  lastUpdated: string
  className?: string
}

export default function OKRDashboard({
  objectives,
  period,
  lastUpdated,
  className
}: OKRDashboardProps) {
  const [expandedObjective, setExpandedObjective] = useState<string | null>(objectives[0]?.id || null)
  const [expandedKR, setExpandedKR] = useState<string | null>(null)
  const [expandedKPI, setExpandedKPI] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'tree' | 'grid'>('tree')

  // Calculate overall progress
  const overallStats = useMemo(() => {
    const totalObjectives = objectives.length
    const achievedObjectives = objectives.filter(o => o.status === 'achieved').length
    const avgProgress = objectives.reduce((sum, o) => sum + o.progress, 0) / totalObjectives
    const atRiskCount = objectives.filter(o => o.status === 'at-risk' || o.status === 'behind').length

    return {
      totalObjectives,
      achievedObjectives,
      avgProgress,
      atRiskCount
    }
  }, [objectives])

  // Get status color and icon
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'achieved':
      case 'good':
      case 'on-track':
        return { color: 'text-status-healthy', bg: 'bg-status-healthy/20', icon: CheckCircle }
      case 'at-risk':
      case 'warning':
        return { color: 'text-status-warning', bg: 'bg-status-warning/20', icon: AlertTriangle }
      case 'behind':
      case 'critical':
        return { color: 'text-status-critical', bg: 'bg-status-critical/20', icon: AlertTriangle }
      default:
        return { color: 'text-surface-400', bg: 'bg-surface-600', icon: Info }
    }
  }

  // Render progress bar
  const ProgressBar = ({ progress, status }: { progress: number; status: string }) => {
    // Status used for styling
    return (
      <div className="w-full h-2 bg-surface-700 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all duration-500',
            status === 'achieved' || status === 'good' || status === 'on-track'
              ? 'bg-status-healthy'
              : status === 'at-risk' || status === 'warning'
              ? 'bg-status-warning'
              : 'bg-status-critical'
          )}
          style={{ width: `${Math.min(100, progress)}%` }}
        />
      </div>
    )
  }

  // Render Contributing Factor
  const renderContributingFactor = (cf: ContributingFactor) => {
    const isInRange = cf.currentValue >= cf.optimalRange.min && cf.currentValue <= cf.optimalRange.max
    const rangePosition = ((cf.currentValue - cf.optimalRange.min) / (cf.optimalRange.max - cf.optimalRange.min)) * 100

    return (
      <div
        key={cf.id}
        className="bg-surface-800/50 rounded-lg p-3 border border-surface-700"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={clsx(
              'w-6 h-6 rounded flex items-center justify-center',
              cf.impact === 'positive' ? 'bg-status-healthy/20 text-status-healthy' :
              cf.impact === 'negative' ? 'bg-status-critical/20 text-status-critical' :
              'bg-surface-600 text-surface-400'
            )}>
              {cf.impact === 'positive' ? (
                <TrendingUp size={12} />
              ) : cf.impact === 'negative' ? (
                <TrendingDown size={12} />
              ) : (
                <Activity size={12} />
              )}
            </div>
            <span className="text-sm font-medium text-surface-200">{cf.factor}</span>
          </div>
          <div className="text-right">
            <div className={clsx(
              'text-sm font-semibold',
              isInRange ? 'text-status-healthy' : 'text-status-warning'
            )}>
              {cf.currentValue.toFixed(1)}
            </div>
            <div className="text-xs text-surface-500">Weight: {(cf.weight * 100).toFixed(0)}%</div>
          </div>
        </div>
        <div className="text-xs text-surface-400 mb-2">{cf.description}</div>
        <div className="relative h-2 bg-surface-700 rounded-full">
          <div
            className="absolute h-full bg-status-healthy/30 rounded-full"
            style={{
              left: '15%',
              width: '70%'
            }}
          />
          <div
            className={clsx(
              'absolute w-3 h-3 rounded-full -top-0.5 transform -translate-x-1/2',
              isInRange ? 'bg-status-healthy' : 'bg-status-warning'
            )}
            style={{ left: `${Math.min(100, Math.max(0, rangePosition * 0.7 + 15))}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-surface-500 mt-1">
          <span>{cf.optimalRange.min}</span>
          <span>{cf.optimalRange.max}</span>
        </div>
      </div>
    )
  }

  // Render KPI
  const renderKPI = (kpi: KPI, _depth: number = 0) => {
    const isExpanded = expandedKPI === kpi.id
    const config = getStatusConfig(kpi.status)
    const progress = (kpi.value / kpi.target) * 100

    return (
      <div key={kpi.id} className="border-l-2 border-surface-700 ml-4 pl-4">
        <button
          onClick={() => setExpandedKPI(isExpanded ? null : kpi.id)}
          className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-surface-700/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', config.bg)}>
              <Gauge size={16} className={config.color} />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-surface-200">{kpi.name}</div>
              <div className="text-xs text-surface-400">
                {kpi.value.toFixed(1)} / {kpi.target} {kpi.unit}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-24">
              <ProgressBar progress={progress} status={kpi.status} />
            </div>
            <span className={clsx('text-sm font-semibold', config.color)}>
              {progress.toFixed(0)}%
            </span>
            {kpi.contributingFactors.length > 0 && (
              isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
            )}
          </div>
        </button>

        {isExpanded && kpi.contributingFactors.length > 0 && (
          <div className="mt-2 ml-4 space-y-2">
            <div className="text-xs text-surface-500 font-medium uppercase tracking-wide mb-2">
              Contributing Factors ({kpi.contributingFactors.length})
            </div>
            <div className="grid gap-2">
              {kpi.contributingFactors.map(renderContributingFactor)}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render Key Result
  const renderKeyResult = (kr: KeyResult) => {
    const isExpanded = expandedKR === kr.id
    const config = getStatusConfig(kr.progress >= 80 ? 'on-track' : kr.progress >= 50 ? 'at-risk' : 'behind')

    return (
      <div key={kr.id} className="border-l-2 border-primary-500/30 ml-4 pl-4">
        <button
          onClick={() => setExpandedKR(isExpanded ? null : kr.id)}
          className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-surface-700/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <Target size={16} className="text-primary-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-surface-200">{kr.title}</div>
              <div className="flex items-center gap-2 text-xs text-surface-400">
                <span>{kr.current.toFixed(1)} / {kr.target} {kr.unit}</span>
                <span className="flex items-center gap-1">
                  {kr.trend === 'up' ? (
                    <TrendingUp size={10} className="text-status-warning" />
                  ) : kr.trend === 'down' ? (
                    <TrendingDown size={10} className="text-status-healthy" />
                  ) : (
                    <Activity size={10} className="text-surface-400" />
                  )}
                  {kr.trendValue > 0 ? '+' : ''}{kr.trendValue.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32">
              <ProgressBar progress={kr.progress} status={kr.progress >= 80 ? 'on-track' : kr.progress >= 50 ? 'at-risk' : 'behind'} />
            </div>
            <span className={clsx('text-sm font-semibold', config.color)}>
              {kr.progress.toFixed(0)}%
            </span>
            {kr.kpis.length > 0 && (
              isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
            )}
          </div>
        </button>

        {isExpanded && kr.kpis.length > 0 && (
          <div className="mt-2 space-y-1">
            <div className="text-xs text-surface-500 font-medium uppercase tracking-wide ml-4 mb-2">
              Key Performance Indicators ({kr.kpis.length})
            </div>
            {kr.kpis.map(kpi => renderKPI(kpi))}
          </div>
        )}
      </div>
    )
  }

  // Render Objective
  const renderObjective = (objective: Objective) => {
    const isExpanded = expandedObjective === objective.id
    const config = getStatusConfig(objective.status)

    return (
      <div
        key={objective.id}
        className={clsx(
          'bg-surface-800/50 rounded-xl border transition-all duration-200',
          isExpanded ? 'border-primary-500/30' : 'border-surface-700'
        )}
      >
        <button
          onClick={() => setExpandedObjective(isExpanded ? null : objective.id)}
          className="w-full p-4 flex items-start justify-between"
        >
          <div className="flex items-start gap-4">
            <div className={clsx(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              config.bg
            )}>
              <Flag size={20} className={config.color} />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-surface-100">{objective.title}</h3>
                <span className={clsx(
                  'px-2 py-0.5 rounded-full text-xs font-medium',
                  config.bg, config.color
                )}>
                  {objective.status.replace('-', ' ').toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-surface-400 mt-1">{objective.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {objective.owner}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {objective.quarter}
                </span>
                <span className="flex items-center gap-1">
                  <GitBranch size={12} />
                  {objective.keyResults.length} Key Results
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={clsx('text-2xl font-bold', config.color)}>
                {objective.progress.toFixed(0)}%
              </div>
              <div className="text-xs text-surface-400">Progress</div>
            </div>
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </button>

        {/* Progress Bar */}
        <div className="px-4 pb-4">
          <ProgressBar progress={objective.progress} status={objective.status} />
        </div>

        {/* Expanded Content - Key Results */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-2">
            <div className="text-xs text-surface-500 font-medium uppercase tracking-wide mb-3">
              Key Results
            </div>
            {objective.keyResults.map(renderKeyResult)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={clsx('bg-surface-800 rounded-xl border border-surface-700 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 border-b border-surface-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Award size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-surface-100">OKR Performance Dashboard</h3>
              <p className="text-sm text-surface-400">{period} | Updated: {lastUpdated}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('tree')}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                viewMode === 'tree' ? 'bg-primary-500/20 text-primary-400' : 'text-surface-400 hover:text-surface-200'
              )}
            >
              <Layers size={18} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                viewMode === 'grid' ? 'bg-primary-500/20 text-primary-400' : 'text-surface-400 hover:text-surface-200'
              )}
            >
              <BarChart3 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-surface-900/30 border-b border-surface-700">
        <div className="text-center">
          <div className="text-2xl font-bold text-surface-100">{overallStats.totalObjectives}</div>
          <div className="text-xs text-surface-400">Total Objectives</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-status-healthy">{overallStats.achievedObjectives}</div>
          <div className="text-xs text-surface-400">Achieved</div>
        </div>
        <div className="text-center">
          <div className={clsx(
            'text-2xl font-bold',
            overallStats.avgProgress >= 80 ? 'text-status-healthy' :
            overallStats.avgProgress >= 50 ? 'text-status-warning' : 'text-status-critical'
          )}>
            {overallStats.avgProgress.toFixed(0)}%
          </div>
          <div className="text-xs text-surface-400">Avg Progress</div>
        </div>
        <div className="text-center">
          <div className={clsx(
            'text-2xl font-bold',
            overallStats.atRiskCount === 0 ? 'text-status-healthy' : 'text-status-warning'
          )}>
            {overallStats.atRiskCount}
          </div>
          <div className="text-xs text-surface-400">At Risk</div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {viewMode === 'tree' ? (
          <div className="space-y-4">
            {objectives.map(renderObjective)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {objectives.map(objective => {
              const config = getStatusConfig(objective.status)
              return (
                <div
                  key={objective.id}
                  className="bg-surface-900/50 rounded-lg p-4 border border-surface-700"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', config.bg)}>
                        <Flag size={16} className={config.color} />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-surface-200">{objective.title}</h4>
                        <div className="text-xs text-surface-500">{objective.keyResults.length} Key Results</div>
                      </div>
                    </div>
                    <div className={clsx('text-xl font-bold', config.color)}>
                      {objective.progress.toFixed(0)}%
                    </div>
                  </div>
                  <ProgressBar progress={objective.progress} status={objective.status} />
                  <div className="flex justify-between mt-2 text-xs text-surface-500">
                    <span>{objective.owner}</span>
                    <span>{objective.quarter}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-surface-700 bg-surface-900/30">
        <div className="flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-healthy" />
            <span className="text-surface-400">On Track / Achieved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-warning" />
            <span className="text-surface-400">At Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-critical" />
            <span className="text-surface-400">Behind Schedule</span>
          </div>
        </div>
      </div>
    </div>
  )
}
